import { Component, HostListener, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Vessel } from 'icreate/common/models/vessel';
import { EapService } from '@service/eap.service';
import { Eap, eapMax, EapScreenDisplayType, eapScreenDisplay } from 'icreate/common/models/eap';
import { MessageDialogService } from '@service/message-dialog.service';
import { lastValueFrom, Subject, take, takeUntil } from 'rxjs';
import {
    ConfirmationWindowComponent,
    ConfirmationWindowTypes,
} from '@components/confirmation-window/confirmation-window.component';
import { getValidationErrors } from 'icreate/common/validators';
import { MappedErrors } from 'icreate/common/validators/custom-functions';
import { eapValidator, eapLocationLenLimit } from 'icreate/common/validators/eap/eap-validator';
import _ from 'lodash';
import { TranslateService } from '@ngx-translate/core';
import { EapSocketService } from '@service/eap-socket.service';

export enum EapInputName {
    location = 'location',
    screenDisplay = 'screenDisplay',
}

@Component({
    selector: 'app-system-setting-eap',
    templateUrl: './system-setting-eap.component.html',
    styleUrls: ['./system-setting-eap.component.css'],
    standalone: false,
})
export class SystemSettingEapComponent implements OnInit {
    readonly EAP_MAX = eapMax;
    readonly LOCATION = EapInputName.location;
    readonly SCREEN_DISPLAY = EapInputName.screenDisplay;

    @Input() vessel!: Vessel;
    @Input() canEditSettings: boolean = true;
    @ViewChild('cancelConfirmation') cancelConfirmation: ConfirmationWindowComponent;

    isOpenOption: boolean = false;
    eapList: Eap[] = [];
    isLoading: boolean = true;
    initEapList: Eap[];
    errorMessagesList: string[] = [];
    showErrors: boolean = false;

    selectedIndex: number = null;

    displays: EapScreenDisplayType[] = [eapScreenDisplay.LCD, eapScreenDisplay.LED];

    validationErrors: { [rowIndex: number]: MappedErrors } = {};
    emptyInputErrors: number = 0;

    private destroySubscriptionSubject$ = new Subject<void>();

    constructor(
        private eapService: EapService,
        private eapSocketService: EapSocketService,
        private messageDialogService: MessageDialogService,
        private translateService: TranslateService
    ) {}

    @HostListener('document:click', ['$event'])
    clickOut(event: MouseEvent) {
        this.isOpenOption = false;
        this.showErrors = false;
    }

    @HostListener('document:wheel')
    @HostListener('document:touchmove')
    handleGlobalEvent() {
        this.showErrors = false;
    }

    async ngOnInit() {
        this.eapList = await this.eapService.getList(this.vessel.id);
        this.initEapList = _.cloneDeep(this.eapList);

        this.eapSocketService
            .eapTableUpdated$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(newEapList => {
                if (this.vessel.id === newEapList[0].vesselId) {
                    this.eapList = newEapList;
                    this.showMessageDialog('system-settings.eap.message-dialog.saved.message');
                    this.initEapList = _.cloneDeep(this.eapList);
                }
            });

        this.isLoading = false;
    }

    ngOnDestroy(): void {
        this.destroySubscriptionSubject$.next();
        this.destroySubscriptionSubject$.complete();
    }

    openMoreOption(index) {
        if (this.selectedIndex == index && this.isOpenOption) {
            this.isOpenOption = false;
            this.selectedIndex = null;
        } else {
            this.isOpenOption = true;
            this.selectedIndex = index;
        }
    }

    onInputChange(event: { name: string; value: any }, eap: Eap, rowIndex: number) {
        if (event.name === this.LOCATION) {
            eap.location = event.value;
        } else if (event.name === this.SCREEN_DISPLAY) {
            eap.screenDisplay = event.value;
        }

        this.checkInputErrors(eap, rowIndex);
    }

    onInputBlur(eap: Eap, rowIndex: number) {
        this.checkInputErrors(eap, rowIndex);
    }

    checkInputErrors(eap: Eap, rowIndex: number) {
        const errors = getValidationErrors(eapValidator(), {
            location: eap.location,
            screenDisplay: eap.screenDisplay,
            index: rowIndex + 1,
        });

        if (!errors || errors.length === 0) {
            delete this.validationErrors[rowIndex];
            return;
        }

        const mappedErrors: MappedErrors = {};

        for (const err of errors) {
            let message = err.message;

            if (message === 'errors.validation.generic.above-maximum') {
                message = this.translateService.instant('errors.validation.generic.above-maximum', {
                    max: eapLocationLenLimit,
                });
            }

            if (!mappedErrors[err.path]) {
                mappedErrors[err.path] = {
                    ...err,
                    message,
                    errors: [message],
                };
            } else {
                mappedErrors[err.path].errors = [...mappedErrors[err.path].errors, message];
            }
        }

        this.validationErrors[rowIndex] = mappedErrors;
    }

    async deleteEap(event: MouseEvent) {
        if (!this.canEditSettings) return;
        event.stopPropagation();
        if (this.selectedIndex !== null && this.selectedIndex >= 0) {
            this.eapList.splice(this.selectedIndex, 1);
            this.selectedIndex = null;
        }
        this.rebuildValidationErrors();
    }

    async duplicateEap(event: MouseEvent) {
        if (!this.canEditSettings) return;
        event.stopPropagation();
        this.isLoading = true;
        try {
            const eapData = {
                ...this.eapList[this.selectedIndex],
                id: '',
                index: this.eapList.length + 1,
            };
            this.eapList.push(eapData);
            this.selectedIndex = null;
            this.isOpenOption = false;
        } catch (error) {
            console.log(error);
        } finally {
            this.isLoading = false;
        }
        this.rebuildValidationErrors();
    }

    async createEapSetting() {
        if (!this.canEditSettings) return;
        const eapData = {
            id: '',
            vesselId: this.vessel.id,
            index: this.eapList.length + 1,
            location:
                this.translateService.instant('system-settings.eap.dialog.input-label.location') +
                ' ' +
                (this.eapList.length + 1),
            screenDisplay: eapScreenDisplay.LCD,
        };

        this.eapList.push(eapData);
    }

    async save() {
        this.isLoading = true;
        try {
            this.eapList.forEach((eap, i) => {
                eap.index = i + 1;
            });

            await this.eapService.save(this.eapList, this.vessel.id);
        } catch (error) {
            console.log(error);
        } finally {
            this.isLoading = false;
        }
    }

    showMessageDialog(message: string, params?: Record<string, any>) {
        this.messageDialogService.openMessage(message, params);
    }

    async exitWithoutSave() {
        if (!this.canEditSettings) return true;
        this.cancelConfirmation.setVisible(true);
        let confirmationOption = await lastValueFrom(this.cancelConfirmation.onOptionSelected.pipe(take(1)));
        if (confirmationOption === ConfirmationWindowTypes.DISCARD) {
            return true;
        } else if (confirmationOption === ConfirmationWindowTypes.GO_BACK) {
            return false;
        } else if (confirmationOption === ConfirmationWindowTypes.SAVE) {
            this.save();
            return true;
        }
    }

    hasChanges() {
        return !_.isEqual(this.initEapList, this.eapList);
    }

    getSaveButtonDisabled() {
        return !this.hasChanges() || this.getEmptyErrors() > 0;
    }

    getCharacterLimit(column: string) {
        return column === this.LOCATION ? eapLocationLenLimit : 16;
    }

    getEmptyErrors(): number {
        let count = 0;

        for (const rowErrors of Object.values(this.validationErrors)) {
            for (const fieldError of Object.values(rowErrors)) {
                if (fieldError?.errors?.includes('errors.validation.generic.missing-required')) {
                    count++;
                }
            }
        }

        return count;
    }

    rebuildValidationErrors() {
        this.validationErrors = {};
        this.eapList.forEach((eap, index) => {
            this.checkInputErrors(eap, index);
        });
    }
}
